// MIELLEY_FOOTER_V2
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { navigate } from '@/hooks/useRoute';
import { useStore } from '@/context/StoreContext';
import { LegalCenter, type LegalDoc } from './LegalCenter';

import './legal-center.css';

type InfoModalType = 'moissanite' | 'availability' | null;

const L = ({
  to,
  children,
  className = '',
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) => (
  <a
    className={className}
    href={to}
    onClick={(e) => {
      e.preventDefault();
      navigate(to);
    }}
  >
    {children}
  </a>
);

export function Footer() {
  const { cms } = useStore();

  const [legal, setLegal] = useState<LegalDoc | null>(null);
  const [infoModal, setInfoModal] = useState<InfoModalType>(null);

  useEffect(() => {
    if (!infoModal) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setInfoModal(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [infoModal]);

  return (
    <>
      {/* =====================================================
          MIELLEY FOOTER V2
         ===================================================== */}

      <footer className="mfy-footer" dir="rtl">

        {/* TRUST STRIP */}
        <div className="mfy-trust-strip">
          <div>
            <b>925</b>
            <span>כסף סטרלינג</span>
          </div>

          <i />

          <div>
            <b>✦</b>
            <span>מוסונייט נבחר</span>
          </div>

          <i />

          <div>
            <b>♡</b>
            <span>שירות אישי</span>
          </div>

          <i />

          <div>
            <b>▣</b>
            <span>אריזה מוקפדת</span>
          </div>
        </div>

        <div className="container mfy-main">

          {/* BRAND */}
          <section className="mfy-brand">

            <img
  src="/assets/mielley-footer-logo.png"
  alt="MIELLEY — LOOK EXPENSIVE. THINK SMART."
  className="mfy-logo"
/>

            <div className="mfy-brand-kicker">
              MOISSANITE · 925 SILVER · RHODIUM
            </div>

            <h2>
              נראה יקר.
              <em> עולה פחות.</em>
            </h2>

            <p>
              תכשיטי מוסונייט בעיצוב נקי ועכשווי,
              כסף סטרלינג 925 וגימור מוקפד —
              עם שירות אישי וחוויית קנייה שקופה.
            </p>

            <div className="mfy-brand-actions">
              <L to="/catalog" className="mfy-primary-cta">
                לגלות את הקולקציה
                <span>←</span>
              </L>

              <L to="/contact" className="mfy-secondary-cta">
                דברו איתנו
              </L>
            </div>

            <div className="mfy-contact">
              <a href={`tel:${cms.site.phone}`}>
                {cms.site.phone}
              </a>

              <span>•</span>

              <a href={`mailto:${cms.site.email}`}>
                {cms.site.email}
              </a>
            </div>
          </section>

          {/* LINKS */}
          <div className="mfy-links">

            {/* COLLECTION */}
            <div className="mfy-column">
              <span className="mfy-column-number">01</span>
              <h4>הקולקציה</h4>

              <L to="/catalog">
                כל תכשיטי המוסונייט
              </L>

              <L to="/catalog?category=rings">
                טבעות מוסונייט
              </L>

              <L to="/catalog?category=earrings">
                עגילי מוסונייט
              </L>

              <L to="/catalog?category=necklaces">
                שרשראות מוסונייט
              </L>

              <L to="/catalog?category=bracelets">
                צמידי מוסונייט
              </L>
            </div>

            {/* DISCOVER */}
            <div className="mfy-column">
              <span className="mfy-column-number">02</span>
              <h4>לגלות</h4>

              <L to="/ring-builder">
                בנו טבעת
              </L>

              <button
                type="button"
                onClick={() => setInfoModal('moissanite')}
              >
                יהלומי מוסונייט
              </button>

              <button
                type="button"
                onClick={() => setInfoModal('availability')}
              >
                זמינות והכנה
              </button>

              <L to="/size-guide">
                מדריך מידות
              </L>

              <L to="/journal">
                המגזין
              </L>
            </div>

            {/* SERVICE */}
            <div className="mfy-column">
              <span className="mfy-column-number">03</span>
              <h4>שירות</h4>

              <L to="/appointments">
                תיאום פגישה
              </L>

              <L to="/contact">
                צור קשר
              </L>

              <L to="/faq">
                שאלות נפוצות
              </L>

              <L to="/account">
                אזור אישי
              </L>

              <button
                type="button"
                onClick={() => setLegal('shipping')}
              >
                משלוחים והחזרות
              </button>
            </div>

            {/* ABOUT */}
            <div className="mfy-column">
              <span className="mfy-column-number">04</span>
              <h4>מידע</h4>

              <L to="/about">
                אודות MIELLEY
              </L>

              <button
                type="button"
                onClick={() => setLegal('terms')}
              >
                תקנון
              </button>

              <button
                type="button"
                onClick={() => setLegal('privacy')}
              >
                פרטיות
              </button>

              <button
                type="button"
                onClick={() => setLegal('accessibility')}
              >
                נגישות
              </button>

              <button
                type="button"
                className="mfy-cancel"
                onClick={() => setLegal('cancel')}
              >
                ביטול עסקה
              </button>
            </div>

          </div>
        </div>

        {/* BOTTOM */}
        <div className="container mfy-bottom">

          <span>
            © {new Date().getFullYear()} MIELLEY
          </span>

          <span className="mfy-bottom-center">
            LOOK EXPENSIVE. THINK SMART.
          </span>

          <span>
            תשלום מאובטח · שירות אישי
          </span>

        </div>
      </footer>

      {/* WHATSAPP */}
      <a
        className="whatsapp"
        href={`https://wa.me/${cms.site.whatsapp}`}
        target="_blank"
        rel="noreferrer"
        aria-label="WhatsApp"
      >
        ☎
      </a>

      {/* LEGAL CENTER */}
      <LegalCenter
        open={legal}
        onClose={() => setLegal(null)}
        onNavigate={setLegal}
        email={cms.site.email}
        phone={cms.site.phone}
      />


      {/* =====================================================
          MOISSANITE MODAL
         ===================================================== */}

      {infoModal === 'moissanite' && (
        <div
          className="mfy-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setInfoModal(null);
            }
          }}
        >
          <section
            className="mfy-info-modal"
            role="dialog"
            aria-modal="true"
            dir="rtl"
          >

            <button
              className="mfy-modal-close"
              type="button"
              onClick={() => setInfoModal(null)}
              aria-label="סגירה"
            >
              ×
            </button>

            <div className="mfy-modal-head">
              <span>
                MIELLEY · STONE GUIDE
              </span>

              <h2>
                יהלומי מוסונייט
              </h2>

              <p>
                הברק שמושך את העין.
                הבחירה שמרגישה חכמה.
              </p>
            </div>

            <div className="mfy-stat-row">

              <div>
                <strong>9.25</strong>
                <b>MOHS</b>
                <small>קשיות גבוהה</small>
              </div>

              <div>
                <strong>✦</strong>
                <b>FIRE</b>
                <small>משחקי אור מרשימים</small>
              </div>

              <div>
                <strong>SiC</strong>
                <b>MOISSANITE</b>
                <small>אבן חן בפני עצמה</small>
              </div>

            </div>

            <div className="mfy-info-grid">

              <article>
                <span>01</span>
                <h3>מהו מוסונייט?</h3>
                <p>
                  מוסונייט הוא אבן חן בעלת ברק עוצמתי
                  ומראה צלול ויוקרתי. המוסונייט המשמש
                  כיום בתכשיטים מיוצר בתנאי מעבדה
                  ומורכב מסיליקון קרביד.
                </p>
              </article>

              <article>
                <span>02</span>
                <h3>האם מוסונייט הוא יהלום?</h3>
                <p>
                  לא. יהלום ומוסונייט הם חומרים שונים.
                  יהלום מורכב מפחמן ואילו מוסונייט
                  מסיליקון קרביד. לכן מוסונייט הוא
                  אבן חן עצמאית ולא יהלום טבעי או מעבדה.
                </p>
              </article>

              <article>
                <span>03</span>
                <h3>למה הוא כל כך נוצץ?</h3>
                <p>
                  למוסונייט תכונות אופטיות היוצרות
                  החזרי אור והבזקי צבע מרשימים.
                  זו אחת הסיבות לנוכחות הבולטת שלו
                  בתכשיטים.
                </p>
              </article>

              <article>
                <span>04</span>
                <h3>מתאים ליום־יום</h3>
                <p>
                  עם קשיות של כ־9.25 בסולם Mohs,
                  מוסונייט נחשב לאבן קשה ועמידה מאוד
                  ומתאים לתכשיטים שנענדים באופן קבוע.
                </p>
              </article>

            </div>

            <div className="mfy-info-note">
              <b>חשוב לדעת</b>

              <p>
                הביטוי „יהלומי מוסונייט” באתר משמש כשם
                שיווקי לקולקציית המוסונייט.
                מבחינה גמולוגית מוסונייט אינו יהלום.
              </p>
            </div>

            <div className="mfy-modal-actions">

              <button
                type="button"
                className="mfy-modal-primary"
                onClick={() => {
                  setInfoModal(null);
                  navigate('/catalog');
                }}
              >
                לכל תכשיטי המוסונייט
                <span>←</span>
              </button>

              <button
                type="button"
                className="mfy-modal-secondary"
                onClick={() => setInfoModal(null)}
              >
                המשך לגלוש
              </button>

            </div>
          </section>
        </div>
      )}


      {/* =====================================================
          AVAILABILITY MODAL
         ===================================================== */}

      {infoModal === 'availability' && (
        <div
          className="mfy-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setInfoModal(null);
            }
          }}
        >
          <section
            className="mfy-info-modal mfy-availability-modal"
            role="dialog"
            aria-modal="true"
            dir="rtl"
          >

            <button
              className="mfy-modal-close"
              type="button"
              onClick={() => setInfoModal(null)}
              aria-label="סגירה"
            >
              ×
            </button>

            <div className="mfy-modal-head">

              <span>
                MIELLEY · AVAILABILITY
              </span>

              <h2>
                התכשיט שלך.
                <br />
                בקצב הנכון.
              </h2>

              <p>
                חלק מהקולקציה כבר מחכה לך.
                <br />
                וחלק ממנה נכין במיוחד עבורך.
              </p>

            </div>

            <div className="mfy-stat-row">

              <div>
                <strong>✦</strong>
                <b>IN STOCK</b>
                <small>
                  מגוון דגמים במלאי
                </small>
              </div>

              <div className="mfy-days-stat">
                <strong>7–14</strong>
                <b>DAYS</b>
                <small>
                  זמן הכנה אפשרי
                </small>
              </div>

              <div>
                <strong>1:1</strong>
                <b>PERSONAL UPDATE</b>
                <small>
                  שירות אישי ועדכון
                </small>
              </div>

            </div>

            <div className="mfy-info-grid">

              <article>
                <span>01</span>
                <h3>
                  מה זמין עכשיו?
                </h3>

                <p>
                  אנחנו מחזיקים מגוון תכשיטי MIELLEY
                  במלאי וזמינים להכנת ההזמנה ומשלוח.
                  הזמינות משתנה לפי הדגם, המידה
                  והווריאציה שנבחרה.
                </p>
              </article>

              <article>
                <span>02</span>
                <h3>
                  ומה אם התכשיט אינו במלאי?
                </h3>

                <p>
                  לא כל פריט המופיע באתר נמצא בכל רגע
                  במלאי פיזי. כאשר הדגם או המידה אינם
                  זמינים, נוכל להזמין או להכין את
                  התכשיט במיוחד עבורך.
                </p>
              </article>

              <article>
                <span>03</span>
                <h3>
                  כמה זמן זה לוקח?
                </h3>

                <p>
                  כאשר נדרשת הכנה או הזמנה מיוחדת,
                  זמן ההכנה הצפוי הוא בדרך כלל
                  בין 7 ל־14 ימים.
                </p>
              </article>

              <article>
                <span>04</span>
                <h3>
                  אנחנו יוצרים קשר
                </h3>

                <p>
                  אם נדרש שינוי, התאמה, בחירת מידה
                  או שהפריט שהוזמן אינו זמין,
                  צוות MIELLEY ייצור קשר כדי לעבור
                  איתך על האפשרויות ולאשר את המשך ההזמנה.
                </p>
              </article>

            </div>

            <div className="mfy-info-note mfy-availability-note">

              <b>
                חשוב לנו שתדעו
              </b>

              <p>
                הצגת מוצר באתר אינה מהווה התחייבות
                לזמינות מיידית במלאי.
                אם הפריט אינו זמין או דורש הכנה
                מיוחדת, ניצור קשר ונעדכן בזמן
                ההכנה הצפוי ובכל הפרטים הרלוונטיים.
              </p>

            </div>

            <div className="mfy-modal-actions">

              <button
                type="button"
                className="mfy-modal-primary"
                onClick={() => {
                  setInfoModal(null);
                  navigate('/catalog');
                }}
              >
                לכל הקולקציה
                <span>←</span>
              </button>

              <button
                type="button"
                className="mfy-modal-secondary"
                onClick={() => setInfoModal(null)}
              >
                המשך לגלוש
              </button>

            </div>
          </section>
        </div>
      )}
    </>
  );
}